import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SectionAlert, Surface, Txt } from '@vst/beam';
import { CheckCircleOutline, ErrorOutline, Router } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import { HeaderWithBackNavigation } from '@app/components/HeaderWithBackNavigation';
import {
  getSsidName,
  resetUpdateSSIDSuccess,
  selectSsid,
  setNetworkError,
  setSsidError,
  setSsidRateLimitError,
  updateSsidInfo,
} from '@app/redux';

import { HomeNetwork } from './home-network';
import { useFormValues } from './useFormValues';
import WifiFooter from './wifi-footer';
import { WifiForm } from './wifi-forms';

import styles from './styles.module.scss';

export const NetworkManagement = () => {
  const { t } = useTranslation('Network');
  const dispatch = useDispatch();
  const { ssidName, ssidLoading, ssidUpdateLoading, error, updateSuccess, ssidRateLimitError } =
    useSelector(selectSsid);

  const { values, handlers, validation, canSave } = useFormValues();

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!ssidName && !ssidLoading) {
      dispatch(getSsidName());
    }
    return () => {
      dispatch(resetUpdateSSIDSuccess());
    };
  }, []);

  useEffect(() => {
    if (ssidName) {
      handlers.onNameChange(ssidName);
    }
  }, [ssidName]);

  useEffect(() => {
    if (!isEditing) {
      handlers.onReset();
    }
  }, [isEditing]);

  useEffect(() => {
    if (updateSuccess) {
      setIsEditing(false);
    }
  }, [updateSuccess, dispatch]);

  const handleEditClick = () => {
    setIsEditing(true);
    if (error) dispatch(setNetworkError(null));
    if (ssidRateLimitError) dispatch(setSsidRateLimitError(false));
    if (updateSuccess) dispatch(resetUpdateSSIDSuccess());
  };

  const handleCancelClick = () => {
    if (ssidRateLimitError) dispatch(setSsidRateLimitError(false));
    setIsEditing(false);
  };

  const handleSave = () => {
    if (canSave) {
      if (error) {
        dispatch(setNetworkError(null));
        dispatch(setSsidError(null));
      }

      dispatch(updateSsidInfo({ ssid: values.wifiName, passphrase: values.newPasswordValue }));
    }
  };

  const showSuccessAlert = updateSuccess && !ssidUpdateLoading;

  return (
    <Surface variant="secondary" pb={{ xs: '4px', md: '72px' }}>
      <HeaderWithBackNavigation
        title={t('networkManagement.pageTitle')}
        handleBackNavigation={() => window.history.back()}
        goBackLabel={t('networkManagement.goBack')}
        subHeader={t('networkManagement.pageSubHeader')}
      />

      <Surface className={styles['container']} variant="secondary">
        {isEditing && (
          <SectionAlert
            type="info"
            icon={Router}
            size="large"
            contentSlot={
              <Txt variant="linkSmall" color="regular">
                <Txt variant="smallBold" component="span">
                  {t('networkManagement.headsUp')}
                </Txt>
                {t('networkManagement.wifiChangeAlert')}
              </Txt>
            }
          />
        )}

        <div>
          {ssidRateLimitError ? (
            <SectionAlert
              type="warning"
              icon={ErrorOutline}
              variant="error"
              size="large"
              onClose={() => {
                dispatch(setSsidRateLimitError(false));
              }}
              mb="16px"
              contentSlot={
                <Txt variant="linkSmall" color="regular">
                  {t('networkManagement.ssidRateLimitError')}
                </Txt>
              }
            />
          ) : error ? (
            <SectionAlert
              type="warning"
              icon={ErrorOutline}
              variant="error"
              size="large"
              onClose={() => {
                dispatch(setNetworkError(null));
              }}
              mb="16px"
              contentSlot={
                <Txt variant="linkSmall" color="regular">
                  {t('networkManagement.ssidError')}
                </Txt>
              }
            />
          ) : showSuccessAlert ? (
            <SectionAlert
              type="success"
              icon={CheckCircleOutline}
              variant="success"
              size="large"
              mb="16px"
              onClose={() => {
                setIsEditing(false);
                dispatch(resetUpdateSSIDSuccess());
              }}
              contentSlot={
                <Txt variant="linkBodyLarge" color="regular">
                  <Txt variant="heading6" component="p">
                    {t('networkManagement.success')}
                  </Txt>
                  {t('networkManagement.ssidSuccess')}
                </Txt>
              }
            />
          ) : null}

          {!isEditing || updateSuccess ? (
            <>
              <SectionAlert
                type="info"
                icon={Router}
                size={'large'}
                mt={'24px'}
                mb={'40px'}
                contentSlot={
                  <Txt variant="linkSmall" color="regular">
                    <Txt variant="smallBold" component="span">
                      {t('networkManagement.headsUp')}
                    </Txt>
                    {t('networkManagement.homeNetworkAlert')}
                  </Txt>
                }
              />
              <HomeNetwork onEditClick={handleEditClick} />
            </>
          ) : (
            <Surface p={{ xs: '24px', md: '40px' }} className={styles['headerContainer']}>
              <WifiForm
                values={values}
                handlers={handlers}
                validation={validation}
                canSave={canSave}
                loading={ssidUpdateLoading}
              />
              <WifiFooter
                onCancel={handleCancelClick}
                onSave={handleSave}
                disabled={!canSave || ssidUpdateLoading}
                loading={ssidUpdateLoading}
              />
            </Surface>
          )}
        </div>
      </Surface>
    </Surface>
  );
};
